import React, { useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, DrawerBody, DrawerFooter, Flex, HStack, Radio, RadioGroup, Text } from '@chakra-ui/react';
import PropTypes, { oneOfType } from 'prop-types';

import CancelAndSaveButtons from '../../../../../../components/CancelAndSaveButtons';
import FormInput from '../../../../../../components/form/FormInput';
import InputLabel from '../../../../../../components/form/InputLabel';
import RQSearchableDropdownMenu from '../../../../../../components/RQSearchableDropdownMenu';
import TrayHeader from '../../../../../../components/Tray/TrayHeader';
import { analyticsIds } from '../../../../../../constants/analyticsIds';
import { getValidationObject } from '../../../../../../constants/regexPatterns';
import { adminPageCreateDepartment, adminPageUpdateDepartment } from '../../../../../../redux/modules/adminPage';
import { setFormIsDirty } from '../../../../../../redux/modules/form';
import { hideTray, updateTrayProps } from '../../../../../../redux/modules/tray';
import { useCompanyActiveUsers } from '../../../../../../services/company-service';

const DepartmentForm = ({ department }) => {
  const dispatch = useDispatch();
  const { companyId } = useParams();

  const reactQueryUsers = useCompanyActiveUsers(companyId);

  const [radioValue, setRadioValue] = useState();
  const [departmentOwner, setDepartmentOwner] = useState(department?.owner);

  const useFormMethods = useForm({
    defaultValues: {
      departmentName: department?.departmentName,
      departmentId: department?.departmentId,
      deptAddress1: department?.deptAddress1,
      deptAddress2: department?.deptAddress2,
      city: department?.deptCity,
      state: department?.deptState,
      deptZip: department?.deptZip
    }
  });

  const { handleSubmit, getValues, reset, control } = useFormMethods;
  const { isDirty, isSubmitting } = useFormState({ control });

  useEffect(() => {
    dispatch(setFormIsDirty(isDirty));
    dispatch(updateTrayProps({ formIsDirty: isDirty }));
  }, [isDirty]);

  let defaultRadio;
  if (department && department.deptAddress1) {
    defaultRadio = 'departmentAddr';
  } else {
    defaultRadio = 'accountAddr';
  }

  useEffect(() => {
    setRadioValue(defaultRadio);
  }, [defaultRadio]);

  useEffect(() => {
    const { departmentName: deptName } = getValues();
    if (radioValue === 'accountAddr') {
      reset({
        departmentName: deptName,
        departmentId: department?.departmentId || '',
        deptAddress1: '',
        deptAddress2: '',
        city: '',
        state: '',
        deptZip: ''
      });
    } else {
      reset({
        departmentName: deptName,
        departmentId: department?.departmentId || '',
        deptAddress1: department?.deptAddress1 || '',
        deptAddress2: department?.deptAddress2 || '',
        city: department?.deptCity || '',
        state: department?.deptState || '',
        deptZip: department?.deptZip || ''
      });
    }
  }, [radioValue, reset]); // eslint-disable-line

  const handleFormSubmit = async data => {
    data.ownerId = departmentOwner?.userId ?? null;

    if (department) {
      await dispatch(adminPageUpdateDepartment(department.departmentId, data));
    } else {
      await dispatch(adminPageCreateDepartment(data));
    }

    dispatch(hideTray());
  };

  const formStack = [
    {
      name: 'departmentName',
      label: 'Deparment Name',
      validation: { required: { value: true, message: 'Department name is required' } },
      isDisabled: false,
      required: true,
      dataAnalyticsId: analyticsIds.adminSettingsPage.departments.createDepartmentForm.name
    },
    {
      name: 'departmentId',
      label: 'Department ID (if applicable)',
      validation: getValidationObject('digits'),
      isDisabled: true,
      required: false,
      dataAnalyticsId: analyticsIds.adminSettingsPage.departments.createDepartmentForm.departmentId
    }
  ];

  const inputDisabled = radioValue === 'accountAddr';

  return (
    <>
      <TrayHeader title={department ? 'Edit Department' : 'New Department'} />
      <DrawerBody>
        <form id="departmentForm" onSubmit={handleSubmit(handleFormSubmit)}>
          {formStack.map(input => (
            <FormInput
              dataAnalyticsId={input.dataAnalyticsId}
              inputName={input.name}
              isDisabled={input.isDisabled}
              key={input.name}
              labelName={input.label}
              placeholder={input.label}
              required={input.required}
              useFormMethods={useFormMethods}
              validationRules={input?.validation}
            />
          ))}

          <Box mb={6} mt="0.3rem">
            <InputLabel
              labelName="Department Owner"
              toolTip="The primary PINS User who is resposible for this department.  If a Department Owner exists, their contact information will be displayed for agents when submitting certificates into PINS if no project owner is selected."
            />
            <RQSearchableDropdownMenu
              getLabel={i => `${i.userFirstName} ${i.userLastName}`}
              idKey="userId"
              reactQuery={reactQueryUsers}
              searchKey="userFirstName"
              selected={departmentOwner}
              width="400px"
              onCancel={() => setDepartmentOwner(null)}
              onSelect={owner => setDepartmentOwner(owner)}
            />
          </Box>

          <InputLabel labelName="Department Address" />
          <RadioGroup
            value={radioValue}
            onChange={setRadioValue}
          >
            <Flex direction="column" mb={5}>
              <Radio
                data-analytics-id={analyticsIds.adminSettingsPage.departments.createDepartmentForm.radioSameAddress}
                dusk={analyticsIds.adminSettingsPage.departments.createDepartmentForm.radioSameAddress}
                value="accountAddr"
              >
                Same as Account Address
              </Radio>
              <Radio
                data-analytics-id={analyticsIds.adminSettingsPage.departments.createDepartmentForm.radioDifferentAddress}
                dusk={analyticsIds.adminSettingsPage.departments.createDepartmentForm.radioDifferentAddress}
                value="departmentAddr"
              >
                Use Different Address
              </Radio>
            </Flex>
          </RadioGroup>
          <FormInput
            dataAnalyticsId={analyticsIds.adminSettingsPage.departments.createDepartmentForm.address1}
            inputName="deptAddress1"
            isDisabled={inputDisabled}
            key="deptAddress1"
            labelName="Address"
            placeholder="Address"
            required={false}
            useFormMethods={useFormMethods}
          />
          <FormInput
            dataAnalyticsId={analyticsIds.adminSettingsPage.departments.createDepartmentForm.address2}
            inputName="deptAddress2"
            isDisabled={inputDisabled}
            key="deptAddress2"
            labelName="Address 2"
            placeholder="Address 2"
            required={false}
            useFormMethods={useFormMethods}
          />
          <FormInput
            dataAnalyticsId={analyticsIds.adminSettingsPage.departments.createDepartmentForm.city}
            inputName="city"
            isDisabled={inputDisabled}
            labelName="City"
            placeholder="City"
            required={false}
            useFormMethods={useFormMethods}
          />
          <HStack alignItems="flex-start" spacing={4} width="100%">
            <FormInput
              dataAnalyticsId={analyticsIds.adminSettingsPage.departments.createDepartmentForm.state}
              inputName="state"
              isDisabled={inputDisabled}
              labelName="State"
              placeholder="State"
              required={false}
              useFormMethods={useFormMethods}
              validationRules={{ maxLength: { value: 2, message: 'Maximum length of 2 characters allowed' } }}
            />
            <FormInput
              dataAnalyticsId={analyticsIds.adminSettingsPage.departments.createDepartmentForm.zip}
              inputName="deptZip"
              isDisabled={inputDisabled}
              labelName="Zip"
              placeholder="Zip"
              required={false}
              useFormMethods={useFormMethods}
              validationRules={{ maxLength: { value: 10, message: 'Maximum length of 10 characters allowed' } }}
            />
          </HStack>
        </form>
      </DrawerBody>
      <DrawerFooter justifyContent="space-between" mb={0} py={4}>
        <CancelAndSaveButtons
          dataAnalyticsId={analyticsIds.adminSettingsPage.departments.createDepartmentForm.saveDepartmentButton}
          formId="departmentForm"
          isDisabled={isSubmitting}
          onClose={() => dispatch(hideTray())}
        />
      </DrawerFooter>
    </>
  );
};

DepartmentForm.propTypes = {
  department: PropTypes.shape({
    departmentName: PropTypes.string,
    departmentId: PropTypes.number,
    deptAddress1: PropTypes.string,
    deptAddress2: PropTypes.string,
    deptCity: PropTypes.string,
    deptState: PropTypes.string,
    deptZip: oneOfType([PropTypes.number, PropTypes.string])
  })
};

export default DepartmentForm;
